package com.eladfinish.bible.contest.screens.search.mvi

import androidx.compose.ui.text.input.TextFieldValue
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.arch.StateKt
import com.eladfinish.bible.contest.common.gender.HebrewGender
import com.eladfinish.bible.contest.data.ChapterData
import com.eladfinish.bible.contest.features.copyOptions.model.CopyOptionsConfig
import com.eladfinish.bible.contest.features.copyOptions.model.CopyOptionsInput
import com.eladfinish.bible.contest.features.copyOptions.model.CopyOptionsOperation
import com.eladfinish.bible.contest.models.ClickableTextData
import com.eladfinish.bible.contest.screens.search.history.SearchHistoryEntry
import com.eladfinish.bible.contest.screens.search.history.SearchHistorySort
import com.eladfinish.bible.contest.screens.search.model.BookResultsCount
import com.eladfinish.bible.contest.screens.search.model.ResultsJump
import com.eladfinish.bible.contest.screens.search.model.SearchMode
import com.eladfinish.bible.contest.screens.search.model.SearchPlace
import com.eladfinish.bible.contest.screens.search.model.Sign
import com.eladfinish.bible.contest.screens.search.model.VerseUiModel
import com.eladfinish.bible.contest.screens.search.suggestions.AutocompleteSuggestion
import com.eladfinish.bible.contest.screens.search.suggestions.PopularLists
import com.eladfinish.bible.contest.screens.search.suggestions.PopularSearchesFilter
import com.eladfinish.bible.contest.screens.search.suggestions.SuggestionsPage
import com.eladfinish.bible.contest.screens.search.usecases.specialSearch.SearchFact
import com.eladfinish.bible.contest.search_results
import dev.icerock.moko.resources.desc.StringDesc
import dev.icerock.moko.resources.desc.desc
import kotlinx.collections.immutable.ImmutableList
import kotlinx.collections.immutable.persistentListOf

data class SearchState(
    val isLoading: Boolean = false,

    // Input
    val textFieldValue: TextFieldValue = TextFieldValue(),
    val specialSigns: ImmutableList<Sign>,
    val isNikudAndTeamimSectionVisible: Boolean = true,
    val errorMessage: StringDesc? = null,

    // Search execution
    val isSearching: Boolean = false,
    val keepRunning: Boolean = true,
    val regex: String = "",
    val results: ImmutableList<VerseUiModel> = persistentListOf(),
    val resultsSummary: StringDesc = SharedRes.strings.search_results.desc(),
    val resultsJump: ResultsJump? = null,

    // Search options
    val searchMode: SearchMode = SearchMode.REGULAR_SEARCH,
    val searchPlace: SearchPlace = SearchPlace.WHOLE_BIBLE,
    val onlyQri: Boolean = false,
    val withNikud: Boolean = true,
    val withTeamim: Boolean = true,
    val withNikudAndTeamim: Boolean = true,

    // Flags / user
    val isSkipReadMeChecked: Boolean = false,
    val shouldShowAiSearch: Boolean = true,
    val searchedData: String = "",
    val isDebugUser: Boolean = false,
    val hebrewGender: HebrewGender = HebrewGender.MALE,

    // Autocomplete (docs/search/search-suggestions-plan.md). The kill switch is read once, on entry.
    val isSearchSuggestionsEnabled: Boolean = false,
    val autocomplete: AutocompleteUiState = AutocompleteUiState.Hidden,
    val suggestionsPage: SuggestionsPage = SuggestionsPage.RESULTS,
    val popularLists: PopularLists? = null,

    // Dialog
    val dialogState: DialogState = DialogState.None,
) : StateKt {

    val query: String
        get() = textFieldValue.text

    val isSearchEnabled: Boolean
        get() = !isSearching

    /** False until a search ran: the summary line only makes sense once there is something to summarize. */
    val hasSearchSummary: Boolean
        get() = resultsSummary != SharedRes.strings.search_results.desc()

    sealed interface DialogState {

        data class FullChapter(
            val chapterData: ChapterData,
        ) : DialogState

        data class InterestingFactsMenu(
            val items: ImmutableList<SearchFact>,
        ) : DialogState

        data class ExecutingLocalSearch(
            val title: StringDesc,
            val titleExtraData: StringDesc? = null,
            val message: StringDesc,
            val totalChapters: Int,
            val progress: Int,
            // Native ad is shown only for a whole-Tanakh search (long enough to load).
            val showNativeAd: Boolean = false,
        ) : DialogState

        data class ExecutingRemoteSearch(
            val sentence: StringDesc,
        ) : DialogState

        data class CopyOptionsDialog(
            val title: StringDesc,
            val input: CopyOptionsInput,
            val config: CopyOptionsConfig,
            val operation: CopyOptionsOperation,
        ) : DialogState

        data class AiSearch(
            val isLoading: Boolean,
            val lastPrompt: String = "",
        ) : DialogState

        data class Help(
            val helpDialogClickableTextData: ClickableTextData,
            val isDontShowAgainChecked: Boolean,
        ) : DialogState

        data class RegexInfo(
            val regex: String,
            val resultsCount: Int,
        ) : DialogState

        /** The bottom sheet that the summary line's "in N books" opens. */
        data class ResultsByBook(
            val items: ImmutableList<BookResultsCount>,
        ) : DialogState

        data class General(
            val title: StringDesc,
            val message: StringDesc,
        ) : DialogState

        data class PopularSearches(
            val filter: PopularSearchesFilter,
            val entries: ImmutableList<AutocompleteSuggestion>,
            val recentDays: Int,
        ) : DialogState

        data class History(
            val entries: ImmutableList<SearchHistoryEntry>,
            val sort: SearchHistorySort,
            val isConfirmingClear: Boolean = false,
        ) : DialogState

        data object None : DialogState
    }
}
